// src/shared/components/TagInputUtils.ts

export interface TagChangeResult {
  /** Cleaned tag list to commit to the form. */
  accepted: string[];
  /** Typed values dropped because they don't match an existing tag (allowNewTags=false only). */
  rejected: string[];
}

/**
 * Pure logic behind TagInput: normalizes a Mantine TagsInput change and
 * reports which typed values were rejected, so the component can surface
 * feedback instead of dropping them silently — vanishing text reads as
 * "tags aren't saving" (a recurring support request).
 */
export class TagInputUtils {
  /**
   * Normalize a TagsInput change: trim, canonicalize casing against allTags,
   * drop case-insensitive duplicates, and (when allowNewTags is false) drop
   * values that don't match an existing tag.
   *
   * Rejections are only reported for values the user just entered — a value
   * already committed (present in previousValue, e.g. a stale tag loaded from
   * saved data) still drops silently rather than flagging a tag the user
   * never typed.
   */
  static processChange(args: {
    newTags: string[];
    allTags: string[];
    allowNewTags: boolean;
    previousValue: string[];
  }): TagChangeResult {
    const { newTags, allTags, allowNewTags, previousValue } = args;
    const previousLower = new Set(previousValue.map((t) => t.toLowerCase()));
    const accepted: string[] = [];
    const rejected: string[] = [];

    for (const tag of newTags) {
      const trimmed = tag.trim();
      if (!trimmed) continue;

      const lower = trimmed.toLowerCase();
      const canonical = allTags.find((t) => t.toLowerCase() === lower);

      if (!allowNewTags && !canonical) {
        const alreadyRejected = rejected.some((t) => t.toLowerCase() === lower);
        if (!previousLower.has(lower) && !alreadyRejected) {
          rejected.push(trimmed);
        }
        continue;
      }

      const finalTag = canonical ?? trimmed;
      const isDuplicate = accepted.some(
        (t) => t.toLowerCase() === finalTag.toLowerCase()
      );
      if (!isDuplicate) accepted.push(finalTag);
    }

    return { accepted, rejected };
  }

  /**
   * Inline feedback for rejected values; null when nothing was rejected.
   * notFoundHint (surface-specific guidance) is appended as a second sentence.
   */
  static formatRejection(
    rejected: string[],
    notFoundHint?: string
  ): string | null {
    if (rejected.length === 0) return null;
    const names = rejected.map((t) => `"${t}"`).join(', ');
    const base =
      rejected.length === 1
        ? `${names} doesn't match an available tag`
        : `${names} don't match available tags`;
    return notFoundHint ? `${base}. ${notFoundHint}` : base;
  }
}
